/**
 * Pinia category-rollup store -- ADR 0059 (Categories & Storefront page).
 *
 * Backs the read-only category count read by the Categories & Storefront
 * nav subtitle (StoreAddEdit.vue) and shared with the section body
 * (StoreCategories/index.vue), mirroring useDecorationRollupStore
 * (stores/decorationRollup.js) so the two "N designs" / "N categories"
 * nav subtitles follow the same pattern:
 *
 *   GET /api/stores/<store_id>/categories
 *   -> [ {id, store_id, name, slug, rich_text, banner_image, hidden,
 *         is_home, nav_order, products, hidden_product_count}, ... ]
 *
 * The section body (StoreCategories/index.vue) owns the full category
 * list -- create/rename/delete/reorder/banner all persist there with its
 * own optimistic-update machinery (accordion state, drag reorder, delete
 * confirmation preview) that is deeply entangled with its local
 * `categories` array. Rather than re-plumb every one of those mutations
 * through this store, the section instead calls `setCount()` here
 * whenever its own list changes (fetch resolves, category added,
 * category deleted) so the nav subtitle stays reactive without a second
 * network round-trip and without touching the section's working
 * add/delete/reorder flows.
 *
 * Tenant + epoch hygiene mirrors stores/decorationRollup.js: an integer
 * ``epoch`` is bumped by ``clear()`` so a late-arriving fetch from a
 * prior tenant/store cannot repopulate state after impersonation stops
 * or the user navigates away.
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import api from "@/services/api";

export const useStoreCategoryRollupStore = defineStore("storeCategoryRollup", () => {
  // Keyed by storeId so navigating between stores doesn't require a
  // fresh Pinia instance.
  const countByStoreId = ref({});
  const loadingByStoreId = ref({});
  const errorByStoreId = ref({});
  const epoch = ref(0);

  function countFor(storeId) {
    return countByStoreId.value[storeId] ?? null;
  }
  function isLoading(storeId) {
    return !!loadingByStoreId.value[storeId];
  }
  function errorFor(storeId) {
    return errorByStoreId.value[storeId] || null;
  }

  // Section-driven update: the section already has the authoritative
  // list (its own GET, or a local optimistic add/delete) -- this just
  // publishes the resulting count so the nav subtitle can read it
  // without a second fetch.
  function setCount(storeId, count) {
    countByStoreId.value = { ...countByStoreId.value, [storeId]: count };
  }

  async function fetchCount(storeId) {
    const startEpoch = epoch.value;
    loadingByStoreId.value = { ...loadingByStoreId.value, [storeId]: true };
    errorByStoreId.value = { ...errorByStoreId.value, [storeId]: null };
    try {
      const { data } = await api.get(`/api/stores/${storeId}/categories`);
      if (startEpoch !== epoch.value) return null;
      const count = Array.isArray(data) ? data.length : 0;
      countByStoreId.value = { ...countByStoreId.value, [storeId]: count };
      return count;
    } catch (err) {
      if (startEpoch !== epoch.value) return null;
      errorByStoreId.value = { ...errorByStoreId.value, [storeId]: err };
      throw err;
    } finally {
      loadingByStoreId.value = { ...loadingByStoreId.value, [storeId]: false };
    }
  }

  function clear() {
    countByStoreId.value = {};
    loadingByStoreId.value = {};
    errorByStoreId.value = {};
    epoch.value += 1;
  }

  return {
    // state
    countByStoreId,
    loadingByStoreId,
    errorByStoreId,
    // getters
    countFor,
    isLoading,
    errorFor,
    // actions
    setCount,
    fetchCount,
    clear,
  };
});
